import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { INVENTORY_AVAILABILITY_URL } from '../../constants';
import type { DeliveryAddressDirectoryEntry, MaterialItem, RegionFieldInfo } from '../../domain/types';
import { materialInventoryNeed } from '../../domain/procurementRequest';
import { deliveryAddressLabelForId } from '../../domain/deliveryAddressDirectory';

export type InventoryAvailabilityLot = {
  inventoryItemId: string;
  inventoryNumber: string;
  quantityOnHand: number;
  footageOnHand?: number | null;
};

export type InventoryAvailabilityLocation = {
  deliveryAddressId?: string | null;
  deliveryAddress: string;
  region: string;
  regionFieldId: string;
  regionFieldName: string;
  afeNumber: string;
  trackFootage: boolean;
  totalQuantityOnHand: number;
  totalFootageOnHand?: number | null;
  lotCount: number;
  lots: InventoryAvailabilityLot[];
};

export type InventoryAvailabilityLine = {
  sku: string;
  materialLineKind?: string | null;
  locations: InventoryAvailabilityLocation[];
};

type AvailabilityResponse = { lines: InventoryAvailabilityLine[] };

export function useRequestInventoryAvailability(
  requestId: string,
  materials: MaterialItem[],
  authFetch: (url: string, init?: RequestInit) => Promise<Response>,
  enabled: boolean
): { loading: boolean; error: string | null; lines: InventoryAvailabilityLine[] } {
  const payload = useMemo(() => {
    const unique = new Map<string, { sku: string; materialLineKind: string }>();
    for (const m of materials) {
      const s = m.sku?.trim();
      if (!s) {
        continue;
      }
      const key = `${s.toLowerCase()}|${m.kind}`;
      if (!unique.has(key)) {
        unique.set(key, { sku: s, materialLineKind: m.kind });
      }
    }
    return { lines: [...unique.values()] };
  }, [materials]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lines, setLines] = useState<InventoryAvailabilityLine[]>([]);

  useEffect(() => {
    if (!enabled || payload.lines.length === 0) {
      setLines([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await authFetch(INVENTORY_AVAILABILITY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          if (!cancelled) {
            setError('Could not load inventory availability.');
            setLines([]);
          }
          return;
        }
        const body = (await res.json()) as AvailabilityResponse;
        if (!cancelled) {
          setLines(body.lines ?? []);
        }
      } catch {
        if (!cancelled) {
          setError('Could not load inventory availability.');
          setLines([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authFetch, enabled, payload, requestId]);

  return { loading, error, lines };
}

function formatOnHand(loc: InventoryAvailabilityLocation, lot?: InventoryAvailabilityLot): string {
  if (loc.trackFootage) {
    const ft = lot?.footageOnHand ?? loc.totalFootageOnHand ?? 0;
    return `${Number(ft).toLocaleString(undefined, { maximumFractionDigits: 2 })} ft`;
  }
  const qty = lot?.quantityOnHand ?? loc.totalQuantityOnHand;
  return `${Number(qty).toLocaleString(undefined, { maximumFractionDigits: 2 })} qty`;
}

function inventoryItemLabel(lot?: InventoryAvailabilityLot): string {
  return lot?.inventoryNumber?.trim() || lot?.inventoryItemId?.trim() || '—';
}

function locationTitle(
  loc: InventoryAvailabilityLocation,
  directory: DeliveryAddressDirectoryEntry[]
): string {
  const id = loc.deliveryAddressId?.trim();
  if (id) {
    const labeled = deliveryAddressLabelForId(id, directory);
    if (labeled && labeled !== '—' && labeled !== id) {
      return labeled;
    }
  }
  return loc.deliveryAddress?.trim() || 'Unspecified location';
}

function categoryHint(
  loc: InventoryAvailabilityLocation,
  directory: DeliveryAddressDirectoryEntry[]
): string {
  const id = loc.deliveryAddressId?.trim();
  if (!id) {
    return '';
  }
  const d = directory.find((x) => x.id === id);
  if (d?.category === 'WellPad') {
    return 'Well pad';
  }
  if (d?.category === 'Yard') {
    return 'Yard';
  }
  return '';
}

function sameDelivery(
  loc: InventoryAvailabilityLocation,
  requestDeliveryAddressId: string | undefined
): boolean {
  const a = loc.deliveryAddressId?.trim();
  const b = requestDeliveryAddressId?.trim();
  if (!a || !b) {
    return false;
  }
  return a.toLowerCase() === b.toLowerCase();
}

export function findAvailabilityForMaterial(
  lines: InventoryAvailabilityLine[],
  mat: MaterialItem
): InventoryAvailabilityLine | undefined {
  const sku = mat.sku?.trim() ?? '';
  if (!sku) {
    return undefined;
  }
  return lines.find(
    (l) =>
      l.sku.toLowerCase() === sku.toLowerCase() &&
      (!l.materialLineKind || l.materialLineKind.toLowerCase() === mat.kind.toLowerCase())
  );
}

export function buildMaterialTransferPrefillHref(args: {
  sku: string;
  kind: string;
  region: string;
  requestNumber?: string;
  requestId?: string;
  from?: InventoryAvailabilityLocation;
  lot?: InventoryAvailabilityLot;
  quantity?: number;
  footage?: number;
  toDeliveryId?: string;
  toFieldId?: string;
  toAfe?: string;
}): string {
  const q = new URLSearchParams();
  if (args.sku) {
    q.set('sku', args.sku);
  }
  if (args.kind) {
    q.set('kind', args.kind);
  }
  if (args.region) {
    q.set('region', args.region);
  }
  if (args.requestNumber) {
    q.set('requestNumber', args.requestNumber);
  }
  if (args.requestId) {
    q.set('requestId', args.requestId);
  }
  if (args.toDeliveryId) {
    q.set('toDeliveryId', args.toDeliveryId);
  }
  if (args.toFieldId) {
    q.set('toFieldId', args.toFieldId);
  }
  if (args.toAfe) {
    q.set('toAfe', args.toAfe);
  }
  if (args.from) {
    if (args.from.deliveryAddressId) {
      q.set('fromDeliveryId', args.from.deliveryAddressId);
    }
    if (args.from.regionFieldId) {
      q.set('fromFieldId', args.from.regionFieldId);
    }
    if (args.from.afeNumber) {
      q.set('fromAfe', args.from.afeNumber);
    }
    const lot = args.lot ?? args.from.lots[0];
    if (lot?.inventoryItemId) {
      q.set('inventoryItemId', lot.inventoryItemId);
    }
  }
  if (args.quantity != null && Number.isFinite(args.quantity) && args.quantity > 0) {
    q.set('quantity', String(args.quantity));
  }
  if (args.footage != null && Number.isFinite(args.footage) && args.footage > 0) {
    q.set('footage', String(args.footage));
  }
  const qs = q.toString();
  return qs ? `/material-transfers/new?${qs}` : '/material-transfers/new';
}

export function RequestLineStockAvailability(props: {
  material: MaterialItem;
  availability?: InventoryAvailabilityLine;
  loading: boolean;
  error: string | null;
  region: string;
  afeNumber: string;
  requestNumber: string;
  requestId: string;
  deliveryAddressId?: string;
  regionFieldInfo?: RegionFieldInfo;
  deliveryAddresses: DeliveryAddressDirectoryEntry[];
  showTransferCta: boolean;
}): ReactElement {
  const {
    material,
    availability,
    loading,
    error,
    region,
    afeNumber,
    requestNumber,
    requestId,
    deliveryAddressId,
    regionFieldInfo,
    deliveryAddresses,
    showTransferCta
  } = props;

  const sku = material.sku?.trim() ?? '';
  const need = materialInventoryNeed(material);
  const neededLabel =
    need.requested > 0
      ? `${need.requested.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${need.unit}`
      : `${material.quantity} ${material.unit?.trim() || 'qty'}`;
  const remainingLabel = `${need.remaining.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${need.unit}`;

  if (!sku) {
    return (
      <div className="request-stock-availability">
        <p className="hint request-stock-availability-empty">
          No SKU on this line — add a SKU to check yard or warehouse stock.
        </p>
      </div>
    );
  }

  const locations = availability?.locations ?? [];
  const atDestination = locations.filter((loc) => sameDelivery(loc, deliveryAddressId));
  const elsewhere = locations.filter((loc) => !sameDelivery(loc, deliveryAddressId));
  const remaining = need.remaining;
  const fullyCovered = remaining <= 0.0001;

  function onHandAmount(loc: InventoryAvailabilityLocation, lot?: InventoryAvailabilityLot): number {
    if (need.trackFootage || loc.trackFootage) {
      return Number(lot?.footageOnHand ?? loc.totalFootageOnHand ?? 0);
    }
    return Number(lot?.quantityOnHand ?? loc.totalQuantityOnHand);
  }

  function lotMoveAmounts(
    from: InventoryAvailabilityLocation,
    lot?: InventoryAvailabilityLot
  ): { quantity?: number; footage?: number } {
    const useLot = lot ?? from.lots[0];
    const lotFt = Number(useLot?.footageOnHand ?? from.totalFootageOnHand ?? 0);
    const lotQty = Number(useLot?.quantityOnHand ?? from.totalQuantityOnHand ?? 0);
    if (need.trackFootage || from.trackFootage) {
      const footage = Math.min(lotFt, remaining);
      let quantity = 0;
      if (lotQty > 0 && lotFt > 0 && footage > 0) {
        quantity = Math.min(lotQty, Math.round((lotQty * footage) / lotFt * 10000) / 10000);
      }
      return { footage, quantity: quantity > 0 ? quantity : undefined };
    }
    return { quantity: Math.min(lotQty, remaining) };
  }

  function vsRequest(loc: InventoryAvailabilityLocation, lot?: InventoryAvailabilityLot): { cls: string; text: string } {
    const onHand = onHandAmount(loc, lot);
    if (fullyCovered) {
      return { cls: 'request-stock-enough', text: 'Already fulfilled' };
    }
    if (onHand + 0.0001 >= remaining) {
      return { cls: 'request-stock-enough', text: 'Enough' };
    }
    const short = remaining - onHand;
    return {
      cls: 'request-stock-short',
      text: `Short ${short.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${need.unit}`
    };
  }

  function transferHref(from?: InventoryAvailabilityLocation, lot?: InventoryAvailabilityLot): string {
    const move = from ? lotMoveAmounts(from, lot) : {};
    return buildMaterialTransferPrefillHref({
      sku,
      kind: material.kind,
      region,
      requestNumber,
      requestId,
      from,
      lot,
      quantity: move.quantity,
      footage: move.footage,
      toDeliveryId: deliveryAddressId,
      toFieldId: regionFieldInfo?.id,
      toAfe: afeNumber
    });
  }

  const stockRows = [...atDestination, ...elsewhere].flatMap((loc) => {
    if (loc.lots.length === 0) {
      return [{ loc, lot: undefined as InventoryAvailabilityLot | undefined }];
    }
    return loc.lots.map((lot) => ({ loc, lot }));
  });

  return (
    <div className="request-stock-availability">
      <h5 className="request-stock-availability-title">Yard / warehouse stock</h5>
      <p className="hint request-stock-availability-need">
        Need {neededLabel} for this request
        {need.fulfilled > 0
          ? ` · ${need.fulfilled.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${need.unit} reserved from yard · remaining ${remainingLabel}`
          : ''}
        .
      </p>
      {fullyCovered && (
        <p className="request-stock-availability-hit">This line is fully covered from yard stock.</p>
      )}
      {loading && <p className="hint">Checking inventory…</p>}
      {error && <p className="error">{error}</p>}
      {!loading && !error && locations.length === 0 && (
        <p className="hint request-stock-availability-empty">
          No on-hand inventory for this SKU in your regions. Continue with quote or purchase if the material must be
          procured.
        </p>
      )}
      {!loading && !error && locations.length > 0 && (
        <>
          {atDestination.length > 0 && (
            <p className="request-stock-availability-hit">
              Stock is already at the request delivery location — a transfer may not be needed.
            </p>
          )}
          <table className="po-lines request-stock-table">
            <thead>
              <tr>
                <th>Location</th>
                <th>Inventory item</th>
                <th>Field</th>
                <th>AFE</th>
                <th>On hand</th>
                <th>Vs request</th>
                {showTransferCta && <th />}
              </tr>
            </thead>
            <tbody>
              {stockRows.map(({ loc, lot }, i) => {
                const atDest = sameDelivery(loc, deliveryAddressId);
                const cat = categoryHint(loc, deliveryAddresses);
                const vs = vsRequest(loc, lot);
                return (
                  <tr key={`${loc.deliveryAddressId ?? loc.deliveryAddress}-${lot?.inventoryItemId ?? loc.afeNumber}-${i}`}>
                    <td>
                      {locationTitle(loc, deliveryAddresses)}
                      {cat ? ` · ${cat}` : ''}
                      {atDest ? ' · request destination' : ''}
                    </td>
                    <td className="mono-id">{inventoryItemLabel(lot)}</td>
                    <td>{loc.regionFieldName || '—'}</td>
                    <td>{loc.afeNumber || '—'}</td>
                    <td>{formatOnHand(loc, lot)}</td>
                    <td>
                      <span className={vs.cls}>{vs.text}</span>
                    </td>
                    {showTransferCta && (
                      <td>
                        {!atDest && !fullyCovered && (
                          <Link className="request-stock-transfer-link" to={transferHref(loc, lot)}>
                            {vs.cls === 'request-stock-short' ? 'Transfer available' : 'Transfer to this request'}
                          </Link>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {showTransferCta && elsewhere.length > 0 && !fullyCovered && (
            <p className="hint">
              Transfer min(on hand, remaining). If the yard is short, this request stays open so you can quote the rest.
            </p>
          )}
        </>
      )}
    </div>
  );
}
